import { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardActionArea, CardContent, Chip, Stack, Typography,
} from '@mui/material';
import { PhotoLibraryOutlined } from '@mui/icons-material';
import PhotoPlaceholder from './PhotoPlaceholder';
import VerifiedChip from './VerifiedChip';
import SaveButton from './SaveButton';
import { fmtFacts, fmtPlace, fmtPrice } from './format';
import { border, brand, status as statusColors, text } from '../../theme/colors';

/**
 * One published listing in the browse grid. Every field comes from the public
 * payload and each one is optional, so the card degrades line by line rather
 * than reserving space for facts the realtor never entered.
 *
 * Save is opt-in: the heart appears only when the page passes `onToggleSave`,
 * which keeps the card usable in contexts with no save story (similar homes on a
 * shared link, the realtor's own previews).
 */
function PropertyCard({
  listing, saved = false, onToggleSave, onRequireLogin, canSave = true,
  hovered = false, onHover,
}) {
  const place = fmtPlace(listing.location);
  const facts = fmtFacts(listing);
  const sold = !!listing.sold_at;
  const agencyName = listing.realtor?.agency?.name || listing.realtor?.display_name || null;
  const eac = listing.realtor?.agency?.status === 'compliant';
  const photoCount = Number(listing.photo_count) || 0;

  return (
    <Card
      variant="outlined"
      onMouseEnter={onHover ? () => onHover(listing.id) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2.5,
        overflow: 'hidden',
        borderColor: hovered ? brand.primary : border.light,
        boxShadow: hovered ? '0 6px 20px rgba(13, 148, 136, 0.18)' : 'none',
        transition: 'box-shadow 160ms ease, border-color 160ms ease',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardActionArea
          component={RouterLink}
          to={`/properties/${listing.id}`}
          sx={{ display: 'block' }}
        >
          {listing.cover_photo ? (
            <Box
              component="img"
              src={listing.cover_photo}
              alt={`Photo of ${listing.title}`}
              sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block', filter: sold ? 'grayscale(0.55)' : 'none' }}
            />
          ) : (
            <PhotoPlaceholder sx={{ aspectRatio: '4 / 3', height: 'auto' }} />
          )}
        </CardActionArea>

        <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 10, left: 10 }}>
          <VerifiedChip />
          {sold && <Chip size="small" label="Sold" sx={{ bgcolor: text.primary, color: '#fff', fontWeight: 700 }} />}
          {listing.tenure_type === 'cession' && (
            <Chip size="small" label="Cession" title="No title deed yet: rights held from a council or developer" sx={{ bgcolor: '#9a3412', color: '#fff', fontWeight: 700 }} />
          )}
          {!sold && listing.is_new && (
            <Chip size="small" label="New" sx={{ bgcolor: brand.secondary, color: '#fff', fontWeight: 700 }} />
          )}
        </Stack>

        {onToggleSave && !sold && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <SaveButton
              saved={saved}
              disabled={!canSave}
              onToggle={() => onToggleSave(listing.id, !saved)}
              onRequireLogin={() => onRequireLogin && onRequireLogin(listing.id)}
            />
          </Box>
        )}

        {photoCount > 1 && (
          <Stack
            direction="row" spacing={0.5} alignItems="center"
            sx={{
              position: 'absolute', bottom: 8, right: 8, px: 1, py: 0.25, borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
            }}
          >
            <PhotoLibraryOutlined sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{photoCount}</Typography>
          </Stack>
        )}
      </Box>

      <CardActionArea
        component={RouterLink}
        to={`/properties/${listing.id}`}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flex: 1, width: '100%', py: 1.75 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800, lineHeight: 1.2,
              color: sold ? text.tertiary : text.primary,
              textDecoration: sold ? 'line-through' : 'none',
            }}
          >
            {fmtPrice(listing.price_usd)}
          </Typography>
          {facts && (
            <Typography variant="body2" sx={{ color: text.primary, mt: 0.5, fontWeight: 500 }}>{facts}</Typography>
          )}
          <Typography
            variant="subtitle2"
            sx={{ color: text.secondary, mt: 0.5, fontWeight: 600, lineHeight: 1.3 }}
          >
            {listing.title}
          </Typography>
          {place && (
            <Typography variant="body2" sx={{ color: text.secondary, mt: 0.25 }}>{place}</Typography>
          )}
          {agencyName && (
            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 1 }}>
              {agencyName}
              {eac && <Box component="span" sx={{ color: statusColors.success, fontWeight: 600 }}> · Registered with EAC</Box>}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// A browse page holds twenty of these and re-renders on every hover; without
// memo, moving the mouse down the list repaints the whole grid.
export default memo(PropertyCard);
